import { useQuery } from '@tanstack/react-query';
import type { MorningSlotDto } from '@upme/contracts';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@upme/ui/components/alert-dialog';
import { Badge } from '@upme/ui/components/badge';
import { Button } from '@upme/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@upme/ui/components/command';
import { Input } from '@upme/ui/components/input';
import { Popover, PopoverContent, PopoverTrigger } from '@upme/ui/components/popover';
import { cn } from '@upme/ui/lib/utils';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { listEventsOptions } from '../../events/queries';
import { useCreateMorningSlot, useDeleteMorningSlot } from '../mutations';
import { useMorningSlots } from '../queries';
import { CityScopeSelect } from './city-scope-select';

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function defaultRange(): { from: string; to: string } {
  const from = new Date();
  const to = new Date();
  to.setDate(to.getDate() + 13);
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

export function MorningSlotsTab() {
  const [scope, setScope] = useState('global');
  const cityId = scope === 'global' ? '' : scope;
  const [range] = useState(defaultRange);
  const { data, isLoading } = useMorningSlots(cityId, range.from, range.to);
  const createSlot = useCreateMorningSlot();
  const deleteSlot = useDeleteMorningSlot();
  const [newDate, setNewDate] = useState(range.from);
  const [newEventId, setNewEventId] = useState<string | null>(null);

  const submit = async () => {
    if (!cityId || !newEventId) return;
    try {
      await createSlot.mutateAsync({ cityId, eventId: newEventId, date: newDate });
      toast.success('Слот создан');
      setNewEventId(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось создать слот');
    }
  };

  const onDelete = async (id: string) => {
    try {
      await deleteSlot.mutateAsync(id);
      toast.success('Слот удалён');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось удалить слот');
    }
  };

  return (
    <div className="space-y-6 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm">Город:</span>
        <CityScopeSelect value={scope} onChange={setScope} />
        <span className="text-xs text-muted-foreground">
          {range.from} — {range.to}
        </span>
      </div>

      {!cityId ? (
        <p className="text-sm text-muted-foreground">Выберите город, чтобы увидеть календарь слотов.</p>
      ) : (
        <>
          <div className="space-y-2 rounded border p-3">
            <div className="text-sm font-medium">Добавить редакторский слот</div>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                className="w-40"
              />
              <EventCombobox cityId={cityId} value={newEventId} onChange={setNewEventId} />
              <Button onClick={submit} disabled={!newEventId || createSlot.isPending}>
                Добавить слот
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div>Загрузка…</div>
          ) : (
            <div className="space-y-4">
              {(data?.days ?? []).map((day) => (
                <div key={day.date} className="rounded border p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-medium">{new Date(day.date).toLocaleDateString('ru-RU')}</span>
                    <span className="text-xs text-muted-foreground">
                      Занято {day.slots.length} из {data?.slotsPerDay ?? 0}, свободно {day.freeSlots}
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {day.slots.length === 0 && <li className="text-xs text-muted-foreground">Пусто</li>}
                    {day.slots.map((slot) => (
                      <SlotRow key={slot.id} slot={slot} onDelete={onDelete} />
                    ))}
                  </ul>
                </div>
              ))}
              {(data?.days ?? []).length === 0 && (
                <p className="text-sm text-muted-foreground">Нет данных за выбранный период.</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SlotRow({ slot, onDelete }: { slot: MorningSlotDto; onDelete: (id: string) => void }) {
  return (
    <li className="flex items-center gap-3 rounded border p-2">
      <span className="flex-1 truncate">{slot.event.title}</span>
      <Badge variant={slot.isPaid ? 'default' : 'secondary'}>
        {slot.isPaid ? 'Оплачено' : 'Редакторский'}
      </Badge>
      {slot.createdByName && <span className="text-xs text-muted-foreground">{slot.createdByName}</span>}
      {slot.isPaid ? (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="sm">
              Удалить
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Удалить оплаченный слот «{slot.event.title}»?</AlertDialogTitle>
              <AlertDialogDescription>
                Слот был оплачен организатором. Если он ещё не был показан в рассылке, энергия будет
                возвращена на баланс организатора. Действие нельзя отменить.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Отмена</AlertDialogCancel>
              <AlertDialogAction onClick={() => onDelete(slot.id)}>Удалить</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : (
        <Button variant="ghost" size="sm" onClick={() => onDelete(slot.id)}>
          Удалить
        </Button>
      )}
    </li>
  );
}

function EventCombobox({
  cityId,
  value,
  onChange,
}: {
  cityId: string;
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data } = useQuery({
    ...listEventsOptions(
      cityId ? { cityId, search: search || undefined, status: 'PUBLISHED', limit: 20 } : {},
    ),
    enabled: Boolean(cityId),
  });

  const items = data?.items ?? [];
  const selected = items.find((e) => e.id === value) ?? null;

  function handleSelect(id: string) {
    onChange(id);
    setOpen(false);
    setSearch('');
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" className="w-72 justify-between">
          <span className="truncate">{selected ? selected.title : 'Выберите событие'}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Поиск события…" value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>Не найдено</CommandEmpty>
            <CommandGroup>
              {items.map((event) => (
                <CommandItem key={event.id} value={event.id} onSelect={() => handleSelect(event.id)}>
                  <Check className={cn('mr-2 h-4 w-4', value === event.id ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">{event.title}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
